import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import Footer from "../components/Footer";
import ChangeEmailModal from "../components/ChangeEmailModal";
import ChangeDisplayNameModal from "../components/ChangeDisplayNameModal";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { useAccount } from "../utils/useAccount";
import { useNavbarHeight } from "../utils/useNavbarHeight";

/**
 * /me/settings - the account details, on their own page.
 *
 * These three cards were a section of /me. They came out because that page is
 * two different jobs: /me is somewhere you go often, to see what happened to
 * your reports, and this is somewhere you go rarely, to change one field. Sat
 * above the feed they were a fixed slab you scrolled past every visit to reach
 * the thing you came for.
 *
 * No brand band here. This is a task page reached from /me, so it opens on the
 * ordinary light head the long-form pages use.
 *
 * The dismiss goes back to whatever sent you, which differs by account type:
 * the navbar takes staff straight here, skipping a /me that holds no feed for
 * them, so returning them to it would land them somewhere they never chose to
 * go. They get the console instead ("/" only bounces there anyway). Reporters
 * came through /me and go back to it.
 */
const AccountSettings = ({ domain, userData, refreshUserData }) => {
  const navigate = useNavigate();
  // Publishes --bl-navbar-h, which `.bl-page--short` subtracts from 100vh.
  useNavbarHeight();
  const { isStaff, isReporter, displayName, email, reload } = useAccount(
    userData,
    refreshUserData,
  );

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const closeEmailModal = () => {
    setShowEmailModal(false);
    reload();
  };

  const saveDisplayName = () => {
    setShowNameModal(false);
    reload();
  };

  return (
    <div className="bleep-light bl-page bl-page--short">
      <Helmet>
        <title>Account Settings | BLEEP</title>
        <meta
          name="description"
          content="Manage your BLEEP email address, display name and password."
        />
      </Helmet>

      <section className="bl-section bl-page__body">
        <div className="bl-shell">
          <div className="bl-feed-head">
            <div className="bl-head-row">
              <h1 className="bl-page-title">Account settings</h1>
              <button
                type="button"
                className="bl-close-btn"
                onClick={() => navigate(isStaff ? "/dashboard" : "/me")}
                aria-label={
                  isStaff
                    ? "Close and go to the dashboard"
                    : "Close and go to my account"
                }
              >
                <i
                  className="fas fa-xmark"
                  aria-hidden="true"
                />
              </button>
            </div>
            <p className="bl-page-sub">
              {isReporter
                ? "Keep your details up to date so we can reach you about your reports."
                : "Keep your sign-in details up to date."}
            </p>
          </div>

          <div className="me-cards">
            <section className="me-card">
              <div className="me-card__head">
                <span
                  className="me-card__icon"
                  aria-hidden="true"
                >
                  <i className="fa-regular fa-envelope" />
                </span>
                <h2 className="me-card__title">Email address</h2>
              </div>
              <p className="me-card__value">{email || "Not set"}</p>
              <p className="me-card__hint">
                Used for notifications about your bleeps and for account
                recovery.
              </p>
              <div className="me-card__foot">
                <button
                  type="button"
                  className="me-btn"
                  onClick={() => setShowEmailModal(true)}
                >
                  <i
                    className="fas fa-pen"
                    aria-hidden="true"
                  />
                  Change email
                </button>
              </div>
            </section>

            <section className="me-card">
              <div className="me-card__head">
                <span
                  className="me-card__icon"
                  aria-hidden="true"
                >
                  <i className="fa-solid fa-id-badge" />
                </span>
                <h2 className="me-card__title">Display name</h2>
              </div>
              <p className="me-card__value">{displayName}</p>
              <p className="me-card__hint">
                How you appear on your bleeps and comments. Anonymous accounts
                show a Bleeper alias instead.
              </p>
              <div className="me-card__foot">
                <button
                  type="button"
                  className="me-btn"
                  onClick={() => setShowNameModal(true)}
                >
                  <i
                    className="fas fa-pen"
                    aria-hidden="true"
                  />
                  Change name
                </button>
              </div>
            </section>

            {/* Reporters sign in with a one-time code sent to their email, so
                they have no password to change. */}
            {isStaff && (
              <section className="me-card">
                <div className="me-card__head">
                  <span
                    className="me-card__icon"
                    aria-hidden="true"
                  >
                    <i className="fa-solid fa-lock" />
                  </span>
                  <h2 className="me-card__title">Password</h2>
                </div>
                <p className="me-card__value">••••••••</p>
                <p className="me-card__hint">
                  Change it regularly, and never reuse a password from another
                  service.
                </p>
                <div className="me-card__foot">
                  <button
                    type="button"
                    className="me-btn"
                    onClick={() => setShowPasswordModal(true)}
                  >
                    <i
                      className="fas fa-pen"
                      aria-hidden="true"
                    />
                    Change password
                  </button>
                </div>
              </section>
            )}
          </div>
        </div>
      </section>

      <Footer domain={domain} />

      <ChangeEmailModal
        domain={domain}
        show={showEmailModal}
        handleClose={closeEmailModal}
        currentEmail={email}
        changeEmail={true}
      />

      <ChangeDisplayNameModal
        domain={domain}
        show={showNameModal}
        handleClose={() => setShowNameModal(false)}
        userData={userData}
        saveDisplayName={saveDisplayName}
      />

      <ChangePasswordModal
        domain={domain}
        show={showPasswordModal}
        handleClose={() => setShowPasswordModal(false)}
        userData={userData}
      />
    </div>
  );
};

export default AccountSettings;
